/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { cancelChallenge, AntiBotChallengeCancelledError } from './antibot-challenge-stash';

/**
 * Backstop for a challenge deferred that can never settle -- the overlay failing
 * to mount, the widget never calling back. Deliberately far longer than any real
 * person needs: at two minutes this cut off someone who was still working through
 * an image challenge, discarded the token they then produced, and left the
 * checkbox on screen looking live. Older and less confident users are exactly who
 * takes longest here and exactly who this product is for, so the deadline exists
 * only to bound the impossible case, never to hurry anyone.
 */
export const CHALLENGE_SOLVE_TIMEOUT_MS = 600_000;

/**
 * Awaits the solved challenge, but never past {@link CHALLENGE_SOLVE_TIMEOUT_MS}.
 *
 * On expiry the pending challenge is cancelled -- which rejects the deferred with
 * AntiBotChallengeCancelledError, the same failure a user-initiated cancel
 * produces, so every caller already handles it -- and the overlay is told the
 * window closed. Expiring silently would leave a checkbox nothing is waiting on:
 * solving it would spend the person's effort on a token that gets dropped.
 *
 * Shared by both anti-bot retry paths (the generated OpenAPI client and the
 * axios/portal-integration wrapper): without it, a 428 on a shell that never
 * mounts the challenge overlay (or any other stuck-forever case) leaves this
 * await pending indefinitely -- a spinner with nothing behind it.
 */
export async function withSolveDeadline(challengeSettled: Promise<string>): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(async () => {
      cancelChallenge('Anti-bot challenge was never solved');
      try {
        const { reportAntiBotError } = await import('@context/AntiBotContext');
        reportAntiBotError(new Error('Challenge expired'), 428, undefined, {
          terminal: true,
          reason: 'expired',
        });
      } catch {
        // AntiBotContext may not be available
      }
      reject(new AntiBotChallengeCancelledError('Anti-bot challenge timed out'));
    }, CHALLENGE_SOLVE_TIMEOUT_MS);
  });
  try {
    await Promise.race([challengeSettled, deadline]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}
